/**
 * Demo Mode Context
 * 
 * Provides centralized control for switching between demo mode (static data)
 * and real mode (API data). This enables the application to work without
 * a running backend while maintaining production-quality code.
 * 
 * @version 1.0.0
 * @date January 11, 2026
 * @task T-S22-P0-001
 * @collaboration Gemini 3 Pro (10/10), GPT 5.2 (9.5/10), DeepSeek V3.2 (9/10)
 */

import { createContext, useContext, useCallback, useMemo, useEffect, useState, type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

/**
 * Demo mode configuration
 */
export interface DemoModeConfig {
  /** Whether demo mode is active */
  isDemoMode: boolean;
  /** Whether to persist mode preference */
  persistPreference: boolean;
  /** Whether API is available (auto-detected) */
  isApiAvailable: boolean;
  /** Whether currently detecting API availability */
  isDetecting: boolean;
}

/**
 * Demo mode context value
 */
export interface DemoModeContextValue extends DemoModeConfig {
  /** Enable demo mode */
  enableDemoMode: () => void;
  /** Disable demo mode (use real API) */
  disableDemoMode: () => void;
  /** Toggle demo mode */
  toggleDemoMode: () => void;
  /** Set demo mode explicitly */
  setDemoMode: (enabled: boolean) => void;
  /** Check API availability */
  checkApiAvailability: () => Promise<boolean>;
}

// ============================================================================
// Constants
// ============================================================================

import { isProduction, isDemoAllowed, getApiUrl } from '../utils/environment';

const STORAGE_KEY = 'raptor_demo_mode';

// In production without VITE_MDC_API_URL, we're in demo mode - no API to check
const API_HEALTH_ENDPOINT = import.meta.env.VITE_MDC_API_URL 
  ? `${import.meta.env.VITE_MDC_API_URL}/api/v1/health`
  : null; // null means no API configured - use demo mode
const API_CHECK_TIMEOUT = 5000; // 5 seconds

// ============================================================================
// Context
// ============================================================================

const DemoModeContext = createContext<DemoModeContextValue | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

export interface DemoModeProviderProps {
  children: ReactNode;
  /** Force demo mode regardless of API availability */
  forceDemoMode?: boolean;
  /** Default mode when no preference stored */
  defaultMode?: boolean;
  /** Whether to persist mode preference in localStorage */
  persistPreference?: boolean;
  /** API health check endpoint override (null means no API configured - use demo mode) */
  apiHealthEndpoint?: string | null;
}

// ============================================================================
// Provider Component
// ============================================================================

/**
 * Demo Mode Provider
 * 
 * Wraps the application to provide demo mode context.
 * Automatically detects API availability and falls back to demo mode.
 */
export function DemoModeProvider({
  children,
  forceDemoMode = false,
  defaultMode = !isProduction(), // In production, default to real API; in dev, default to demo
  persistPreference = true,
  apiHealthEndpoint = API_HEALTH_ENDPOINT,
}: DemoModeProviderProps): JSX.Element {
  // State
  const [isDemoMode, setIsDemoModeState] = useState<boolean>(() => {
    // Check environment variable first
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      return true;
    }
    if (import.meta.env.VITE_DEMO_MODE === 'false') {
      return false;
    }
    
    // Check forced mode
    if (forceDemoMode) {
      return true;
    }
    
    // Check persisted preference
    if (persistPreference && typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        return stored === 'true';
      }
    }
    
    // Use default
    return defaultMode;
  });
  
  const [isApiAvailable, setIsApiAvailable] = useState<boolean>(false);
  const [isDetecting, setIsDetecting] = useState<boolean>(true);
  
  // ========================================================================
  // API Availability Check
  // ========================================================================
  
  const checkApiAvailability = useCallback(async (): Promise<boolean> => {
    setIsDetecting(true);
    
    // If demo mode is explicitly enabled via environment variable, skip ALL API checks
    if (import.meta.env.VITE_DEMO_MODE === 'true' || import.meta.env.VITE_DEMO_MODE === true) {
      console.log('[DemoMode] Demo mode enabled via VITE_DEMO_MODE - skipping API checks');
      setIsApiAvailable(false);
      setIsDemoModeState(true);
      setIsDetecting(false);
      return false;
    }
    
    // If no API endpoint is configured (production demo mode), skip the check
    // and use demo mode directly
    if (!apiHealthEndpoint) {
      console.log('[DemoMode] No API endpoint configured - using demo mode');
      setIsApiAvailable(false);
      setIsDemoModeState(true);
      setIsDetecting(false);
      return false;
    }
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CHECK_TIMEOUT);
      
      const response = await fetch(apiHealthEndpoint, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      });
      
      clearTimeout(timeoutId);
      
      const available = response.ok;
      setIsApiAvailable(available);
      
      // If API is available but we're in demo mode due to previous unavailability,
      // we keep the user's choice unless they haven't made one
      if (available && !persistPreference) {
        setIsDemoModeState(false);
      }
      
      return available;
    } catch (error) {
      // API not available
      setIsApiAvailable(false);
      
      // In production without a configured API, use demo mode
      // This is the expected state when backend isn't deployed yet
      if (!import.meta.env.VITE_MDC_API_URL) {
        console.log('[DemoMode] Production demo mode - no backend configured');
        setIsDemoModeState(true);
        return false;
      }
      
      // SECURITY: In production WITH a configured API that's unavailable,
      // DO NOT silently fall back to demo mode
      if (isProduction()) {
        console.error('[DemoMode] PRODUCTION: API unavailable - NOT falling back to demo mode');
        // Keep current mode (real) - let the app show proper error states
        return false;
      }
      
      // Only auto-switch to demo mode in development environments
      if (!isDemoMode) {
        console.log('[DemoMode] DEV: API unavailable, switching to demo mode');
        setIsDemoModeState(true);
      }
      
      return false;
    } finally {
      setIsDetecting(false);
    }
  }, [apiHealthEndpoint, isDemoMode, persistPreference]);
  
  // Check API availability on mount
  useEffect(() => {
    void checkApiAvailability();
    
    // Periodically check API availability
    const interval = setInterval(() => {
      void checkApiAvailability();
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [checkApiAvailability]);
  
  // ========================================================================
  // Mode Control Functions
  // ========================================================================
  
  const setDemoMode = useCallback((enabled: boolean) => {
    setIsDemoModeState(enabled);
    
    if (persistPreference && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, String(enabled));
    }
    
    console.log(`[DemoMode] Mode set to: ${enabled ? 'DEMO' : 'REAL'}`);
  }, [persistPreference]);
  
  const enableDemoMode = useCallback(() => {
    setDemoMode(true);
  }, [setDemoMode]);
  
  const disableDemoMode = useCallback(() => {
    if (!isApiAvailable) {
      console.warn('[DemoMode] Cannot disable demo mode - API not available');
      return;
    }
    setDemoMode(false);
  }, [setDemoMode, isApiAvailable]);
  
  const toggleDemoMode = useCallback(() => {
    if (isDemoMode) {
      disableDemoMode();
    } else {
      enableDemoMode();
    }
  }, [isDemoMode, enableDemoMode, disableDemoMode]);
  
  // ========================================================================
  // Context Value
  // ========================================================================
  
  const contextValue = useMemo<DemoModeContextValue>(() => ({
    isDemoMode,
    persistPreference,
    isApiAvailable,
    isDetecting,
    enableDemoMode,
    disableDemoMode,
    toggleDemoMode,
    setDemoMode,
    checkApiAvailability,
  }), [
    isDemoMode,
    persistPreference,
    isApiAvailable,
    isDetecting,
    enableDemoMode,
    disableDemoMode,
    toggleDemoMode,
    setDemoMode,
    checkApiAvailability,
  ]);
  
  return (
    <DemoModeContext.Provider value={contextValue}>
      {children}
    </DemoModeContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access demo mode context
 * 
 * @returns Demo mode context value
 * @throws Error if used outside DemoModeProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isDemoMode, toggleDemoMode } = useDemoMode();
 *   
 *   return (
 *     <button onClick={toggleDemoMode}>
 *       Mode: {isDemoMode ? 'Demo' : 'Real'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useDemoMode(): DemoModeContextValue {
  const context = useContext(DemoModeContext);
  
  if (context === null) {
    throw new Error(
      'useDemoMode must be used within a DemoModeProvider. ' +
      'Wrap your app in <DemoModeProvider> to use this hook.'
    );
  }
  
  return context;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if running in demo mode (synchronous, uses env var)
 * 
 * Use this for initial render decisions before context is available.
 * For reactive updates, use the useDemoMode() hook instead.
 */
export function isDemoModeEnabled(): boolean {
  // Check environment variable
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    return true;
  }
  if (import.meta.env.VITE_DEMO_MODE === 'false') {
    return false;
  }
  
  // Check localStorage
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      return stored === 'true';
    }
  }
  
  // Use centralized environment check - demo only allowed in dev/test
  return isDemoAllowed();
}

// ============================================================================
// Display Name for DevTools
// ============================================================================

DemoModeContext.displayName = 'DemoModeContext';

export default DemoModeProvider;
